import { Checkbox, Dropdown, TooltipV2 } from '@cimplico/components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { useUpdateWorkpaperProcedures } from '../../queries/use-update-workpaper-procedures';
import { WorkpaperProcedure, WorkpaperProcedureStatusEnum } from '../../types/workpaper-procedure';
import { isRequiredRole } from '../../utilities/is-required-role';
import { useSearchParams } from 'react-router-dom';
import { calcRelativeTime, formatDateTime, formatMixedCase } from '../../utilities/formatting';
import WorkpaperProcedureActiveConfirmDialog from './WorkpaperProcedureActiveConfirmDialog';
import WorkpaperProcedureDeleteConfirmDialog from './WorkpaperProcedureDeleteConfirmDialog';
import WorkpaperProcedureNotApplicableConfirmDialog from './WorkpaperProcedureNotApplicableConfirmDialog';
import TaskCheckbox from '../../components/task-checkbox/TaskCheckbox';
import UserAvatar from '../../components/avatar/UserAvatar';
import WorkpaperProcedureMeta from './WorkpaperProcedureMeta';
import { useIntercom } from 'react-use-intercom';
import { intercomEvents } from '../../intercom-events';

/**
 * Get status for the procedure
 *
 * @param status WorkpaperProcedureStatusEnum
 * @returns isActive is true if status is ACTIVE / isChecked is true if status is CHECKED / isNotApplicable is true if status is NOT_APPLICABLE
 */
function getProcedureStatus(status: WorkpaperProcedureStatusEnum): { isActive: boolean; isChecked: boolean; isNotApplicable: boolean } {
    return {
        isActive: status === WorkpaperProcedureStatusEnum.ACTIVE,
        isChecked: status === WorkpaperProcedureStatusEnum.CHECKED,
        isNotApplicable: status === WorkpaperProcedureStatusEnum.NOT_APPLICABLE,
    };
}

type WorkpaperProcedureProps = {
    workpaperProcedure: WorkpaperProcedure;
    selectedProcedures: WorkpaperProcedure[];
    onProcedureChecked: (procedure: WorkpaperProcedure) => void;
};

function WorkpaperProcedureRow({ workpaperProcedure, selectedProcedures, onProcedureChecked }: WorkpaperProcedureProps): JSX.Element {
    const procedureId = workpaperProcedure.id;

    const [isOpenActiveDialog, setIsOpenActiveDialog] = useState(false);
    const [isOpenNotApplicableDialog, setIsOpenNotApplicableDialog] = useState(false);
    const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);

    const updateProceduresStatusQuery = useUpdateWorkpaperProcedures(workpaperProcedure.workpaperId);

    const { role, readOnly } = useWorkpaperContext();
    const { isActive, isChecked, isNotApplicable } = getProcedureStatus(workpaperProcedure.status);

    const [query, setQuery] = useSearchParams();
    const activeProcedureId = query.get('activeProcedure');
    const isFocused = activeProcedureId === procedureId;

    const taskCheckboxRef = useRef<HTMLDivElement>(null);
    const procedureItemRef = useRef<HTMLDivElement>(null);
    const dropdownTriggerRef = useRef<HTMLDivElement>(null);

    const { trackEvent } = useIntercom();
    /**
     * Toggles a procedure between CHECKED and ACTIVE
     */
    function toggleProcedureCheck(): void {
        if (!isChecked) {
            trackEvent(intercomEvents.procedure.checked.event);
        }
        updateProceduresStatusQuery.mutate([
            { id: procedureId, fields: { status: isChecked ? WorkpaperProcedureStatusEnum.ACTIVE : WorkpaperProcedureStatusEnum.CHECKED } },
        ]);
    }

    function handleStatusChange(status: 'CHECKED' | 'UNCHECKED' | 'NOT_APPLICABLE'): void {
        if (status === 'NOT_APPLICABLE') {
            setIsOpenActiveDialog(true);
            return;
        }
        toggleProcedureCheck();
    }

    function getDisabledHint(prefix: string): string | undefined {
        if (isRequiredRole(role, workpaperProcedure.role)) {
            return;
        }

        return `${prefix} as your role of ${formatMixedCase(role.toString())} cannot complete items for ${formatMixedCase(
            workpaperProcedure.role.toString()
        )}.`;
    }

    const handleProcedureItemClick = useCallback(
        (event: MouseEvent) => {
            if (!(event.target instanceof Element)) {
                return;
            }
            if (
                procedureItemRef.current &&
                (procedureItemRef.current.contains(event.target) || procedureItemRef.current.isEqualNode(event.target)) &&
                taskCheckboxRef.current &&
                !(taskCheckboxRef.current.contains(event.target) || taskCheckboxRef.current.isEqualNode(event.target)) &&
                dropdownTriggerRef.current &&
                !(dropdownTriggerRef.current.contains(event.target) || dropdownTriggerRef.current.isEqualNode(event.target))
            ) {
                if (procedureId === activeProcedureId) {
                    setQuery((prev) => {
                        prev.delete('addon');
                        prev.delete('activeProcedure');
                        return prev;
                    });
                    return;
                }
                setQuery((prev) => {
                    prev.set('addon', 'procedureDetails');
                    prev.set('activeProcedure', procedureId);
                    return prev;
                });
            }
        },
        [activeProcedureId, procedureId, setQuery]
    );

    useEffect(() => {
        document.addEventListener('mousedown', handleProcedureItemClick);

        return (): void => {
            document.removeEventListener('mousedown', handleProcedureItemClick);
        };
    }, [handleProcedureItemClick]);

    return (
        <>
            {/* ACTIVE CONFIRMATION */}
            <WorkpaperProcedureActiveConfirmDialog
                isOpen={isOpenActiveDialog}
                procedureIds={[procedureId]}
                workpaperId={workpaperProcedure.workpaperId}
                onClose={(): void => setIsOpenActiveDialog(false)}
                onUpdate={(): void => setIsOpenActiveDialog(false)}
            />
            {/* NOT APPLICABLE CONFIRMATION */}
            <WorkpaperProcedureNotApplicableConfirmDialog
                isOpen={isOpenNotApplicableDialog}
                procedureIds={[procedureId]}
                workpaperId={workpaperProcedure.workpaperId}
                onClose={(): void => setIsOpenNotApplicableDialog(false)}
                onUpdate={(): void => setIsOpenNotApplicableDialog(false)}
            />
            {/* DELETE CONFIRMATION */}
            <WorkpaperProcedureDeleteConfirmDialog
                isOpen={isOpenDeleteDialog}
                onClose={(): void => setIsOpenDeleteDialog(false)}
                procedureId={procedureId}
                workpaperId={workpaperProcedure.workpaperId}
            />

            <div className="flex items-center gap-2 group">
                <div className={`${selectedProcedures.length === 0 ? 'invisible' : ''} group-hover:visible`}>
                    <Checkbox
                        disabled={readOnly}
                        checked={selectedProcedures.find((p) => p.id === procedureId) !== undefined}
                        onChange={(): void => onProcedureChecked(workpaperProcedure)}
                    />
                </div>
                <div
                    ref={procedureItemRef}
                    className={`cursor-pointer relative p-4 flex-1 rounded-[5px] border border-gray-200 ${
                        isFocused ? 'bg-indigo-50' : isActive ? 'bg-white' : 'bg-gray-50'
                    }`}
                >
                    {!isActive && (
                        <div className={`absolute top-0 left-0 ${isChecked ? 'bg-green-600' : 'bg-red-600'} rounded-l-[5px] h-full w-1`} />
                    )}
                    <div className="flex items-bottom justify-between">
                        <div className="flex items-start gap-2 max-w-[70%]">
                            <div ref={taskCheckboxRef}>
                                <TaskCheckbox
                                    status={isNotApplicable ? 'NOT_APPLICABLE' : isActive ? 'UNCHECKED' : 'CHECKED'}
                                    disabled={readOnly || !isRequiredRole(role, workpaperProcedure.role)}
                                    onChange={handleStatusChange}
                                    disabledHint={getDisabledHint('Cannot complete the procedure')}
                                />
                            </div>
                            <p className="text-gray-500 text-sm font-normal">{workpaperProcedure.reference}</p>
                            <div className="flex flex-col gap-2">
                                <div className="flex flex-col gap-1">
                                    <p className={`${!isActive ? 'text-gray-700' : 'text-gray-800'} text-sm`}>{workpaperProcedure.name}</p>
                                    <p className={`${!isActive ? 'text-gray-500' : 'text-gray-600'} text-xs whitespace-pre-line`}>
                                        {workpaperProcedure.description}
                                    </p>
                                </div>
                                <WorkpaperProcedureMeta workpaperProcedure={workpaperProcedure} />
                            </div>
                        </div>
                        <div className="flex flex-col gap-2 items-end">
                            <div ref={dropdownTriggerRef}>
                                <TooltipV2 disabled={!readOnly} message="This workpaper is read only." position="right">
                                    <Dropdown direction="bottom" disabled={readOnly}>
                                        {isNotApplicable ? (
                                            <Dropdown.Option
                                                label="Mark as Applicable"
                                                disabled={!isRequiredRole(role, workpaperProcedure.role)}
                                                disabledHint={getDisabledHint('Cannot mark the procedure as applicable')}
                                                onClick={(): void => setIsOpenActiveDialog(true)}
                                                value="applicable"
                                            />
                                        ) : (
                                            <Dropdown.Option
                                                label="Not applicable"
                                                disabled={!isRequiredRole(role, workpaperProcedure.role)}
                                                disabledHint={getDisabledHint('Cannot mark the procedure as not applicable')}
                                                onClick={(): void => setIsOpenNotApplicableDialog(true)}
                                                value="notApplicable"
                                            />
                                        )}
                                    </Dropdown>
                                </TooltipV2>
                            </div>
                            <div className="mt-auto flex items-center gap-2">
                                {workpaperProcedure.userAction && (
                                    <>
                                        <TooltipV2 message={formatDateTime(workpaperProcedure.userAction.date)}>
                                            <p className="text-gray-500 text-xs ml-2">
                                                {calcRelativeTime(workpaperProcedure.userAction.date)}
                                            </p>
                                        </TooltipV2>

                                        {workpaperProcedure.userAction && (
                                            <UserAvatar
                                                size="mini"
                                                tooltip={`${workpaperProcedure.userAction.firstName} ${workpaperProcedure.userAction.lastName}`}
                                                tooltipDirection="left"
                                                user={workpaperProcedure.userAction}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default WorkpaperProcedureRow;
